import type { z } from 'zod';
import type { ApiKeySchema, ApiKeyCreatedEnvelope, ApiKeyRevokedEnvelope } from '@contract/apiKeys';
import { apiClient } from './api';

/**
 * REV-821: the response types come from the vendored contract, which
 * revenant-api checks against its real routes. The hand-typed versions had
 * drifted: they had no `scopes` (REV-438) and typed `revoked_by` as an
 * optional string where the API sends null.
 */
export type ApiKey = z.infer<typeof ApiKeySchema>;
export type CreateApiKeyResult = z.infer<typeof ApiKeyCreatedEnvelope>['data'];
export type RevokeApiKeyResult = z.infer<typeof ApiKeyRevokedEnvelope>['data'];

export interface CreateApiKeyData {
  name: string;
  expiresAt?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  requestId?: string;
}

export const apiKeyService = {
  list: async (orgId: string) => {
    const response = await apiClient.get<ApiResponse<ApiKey[]>>(
      `/v1/organizations/${orgId}/api-keys`
    );
    return response.data;
  },

  getById: async (orgId: string, keyId: string) => {
    const response = await apiClient.get<ApiResponse<ApiKey>>(
      `/v1/organizations/${orgId}/api-keys/${keyId}`
    );
    return response.data;
  },

  create: async (orgId: string, data: CreateApiKeyData) => {
    const response = await apiClient.post<ApiResponse<CreateApiKeyResult>>(
      `/v1/organizations/${orgId}/api-keys`,
      data
    );
    return response.data;
  },

  revoke: async (orgId: string, keyId: string) => {
    const response = await apiClient.post<ApiResponse<RevokeApiKeyResult>>(
      `/v1/organizations/${orgId}/api-keys/${keyId}/revoke`
    );
    return response.data;
  },
};

export default apiKeyService;
